import { RootState } from "@store/reducers";
import { EnumUserTypes, RecruiterTypes } from "@utils/constants";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { Outlet, useLocation } from "react-router-dom";
import {
  candidateTabs,
  hiringCompanyTabs,
  recruiterTabs,
  referralTabs,
} from "./settings.tabs";

const tabs = [
  {
    heading: "Account",
    route: "account",
    children: [
      {
        label: "Personal Details",
        route: "personal-details",
      },
      {
        label: "Username",
        route: "username",
      },
      {
        label: "Change Password",
        route: "change-password",
      },
    ],
  },
  {
    heading: "Company",
    route: "company",
    children: [
      {
        label: "Company Details",
        route: "company-details",
      },
      {
        label: "Account Members",
        route: "account-members",
      },
    ],
  },
];

const SettingsLayout = () => {
  const { pathname } = useLocation();
  const userType = useSelector(
    (state: RootState) => state.auth?.user?.userType,
  );
  const recruiterType = useSelector(
    (state: RootState) => state.auth?.user?.recruiterType,
  );
  const tabs = useMemo(() => {
    if (userType === EnumUserTypes.CANDIDATE) {
      return candidateTabs;
    } else if (userType === EnumUserTypes.HIRING_COMPANY) {
      return hiringCompanyTabs;
    } else if (userType === EnumUserTypes.RECRUITER) {
      if (recruiterType === RecruiterTypes.MEMBER) {
        return recruiterTabs.map((tab) => {
          if (tab.route !== "company") return tab;
          return {
            ...tab,
            children: tab.children.filter(
              (child) => child.route !== "account-members",
            ),
          };
        });
      }
      return recruiterTabs;
    } else if (userType === EnumUserTypes.REFERRAL_AMBASSADOR) {
      return referralTabs;
    }
  }, [userType, recruiterType]);

  const activeTab = useMemo(() => {
    if (pathname.includes("/settings/company")) return tabs[1];
    return tabs[0];
  }, [pathname]);

  return (
    <div className="flex flex-col flex-1 rounded-md bg-white p-5">
      {/* Tab section */}
      <div className="flex items-center py-4 pl-60 border-b gap-12">
        {tabs?.map((tab) => {
          return (
            <NavLink
              key={tab?.heading}
              to={tab?.route}
              className={({ isActive }) =>
                isActive
                  ? "font-semibold text-sm text-secondary border-b-2 border-light-gray"
                  : "font-medium text-xs text-light-black"
              }
            >
              {tab?.heading}
            </NavLink>
          );
        })}
      </div>
      <div className="flex flex-1 min-h-0">
        {/* Sub-tab section */}
        <div className="flex flex-col border-r py-5 w-56 px-2">
          {activeTab?.children?.map((subTab) => {
            return (
              <NavLink
                key={subTab?.label}
                to={`${activeTab?.route}/${subTab?.route}`}
                className={({ isActive }) =>
                  `font-medium text-xs text-left px-2 py-3 rounded-md ${
                    isActive
                      ? "bg-gray-100"
                      : "text-light-black hover:bg-gray-50"
                  }`
                }
              >
                {subTab?.label}
              </NavLink>
            );
          })}
        </div>
        <div className="flex flex-col min-h-0 p-5 flex-1 overflow-y-scroll no-scrollbar">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default SettingsLayout;
